import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getCourseByPath } from '../services/courseService';
import { resolveMediaUrl } from '../utils/media';
import './CourseDetail.css';

function CourseDetail() {
  const { path } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (path) fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCourseByPath(path);
      if (response.success) {
        setCourse(response.data);
      } else {
        setError('Course not found');
      }
    } catch (err) {
      console.error('Error fetching course:', err);
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);

  const getCategoryColor = (category) => category?.color || '#667eea';

  const getCourseImage = (course) => resolveMediaUrl(course?.image);

  const getDiscount = (price, originalPrice) => {
    if (!originalPrice || originalPrice <= price) return null;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="cd-loading">
          <div className="spinner"></div>
          <p>Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-detail-page">
        <div className="cd-error">
          <span className="cd-error-icon">😕</span>
          <h2>Course Not Found</h2>
          <p>{error || 'The course you are looking for does not exist.'}</p>
          <button className="cd-back-btn" onClick={() => navigate('/courses')}>
            ← Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const discount = getDiscount(course.price, course.originalPrice);
  const courseImage = getCourseImage(course);
  const categoryColor = getCategoryColor(course.category);

  const siteName = 'Durbhasi Gurukulam';
  const seoTitle = (course.seoTitle || '').trim() || `${course.title} | ${siteName}`;
  const seoDescription = (
    (course.seoDescription || '').trim() ||
    (course.shortDescription || '').trim() ||
    (course.description || '').trim().slice(0, 180)
  );
  const seoOgImage = resolveMediaUrl((course.seoOgImage || '').trim() || course.image);
  const computedCanonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/courses/${encodeURIComponent(course.path)}`
      : '';
  const seoCanonicalUrl = (course.seoCanonicalUrl || '').trim() || computedCanonicalUrl;
  const seoNoindex = Boolean(course.seoNoindex);

  return (
    <div className="course-detail-page">
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription ? <meta name="description" content={seoDescription} /> : null}
        {seoCanonicalUrl ? <link rel="canonical" href={seoCanonicalUrl} /> : null}

        <meta property="og:site_name" content={siteName} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        {seoDescription ? <meta property="og:description" content={seoDescription} /> : null}
        {seoCanonicalUrl ? <meta property="og:url" content={seoCanonicalUrl} /> : null}
        {seoOgImage ? <meta property="og:image" content={seoOgImage} /> : null}

        <meta name="twitter:card" content={seoOgImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        {seoDescription ? <meta name="twitter:description" content={seoDescription} /> : null}
        {seoOgImage ? <meta name="twitter:image" content={seoOgImage} /> : null}

        {seoNoindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      </Helmet>
      {/* Hero Section */}
      <div className="cd-hero" style={{ background: `linear-gradient(135deg, ${categoryColor}cc 0%, #1a1a2e 100%)` }}>
        <div className="container">
          <button className="cd-back-btn" onClick={() => navigate('/courses')}>
            ← Back to Courses
          </button>

          <div className="cd-hero-content">
            <div className="cd-hero-info">
              <div className="cd-badges">
                <span className="cd-category-badge" style={{ backgroundColor: categoryColor }}>
                  {course.category?.icon && <span>{course.category.icon}</span>}
                  {course.category?.name || 'General'}
                </span>
                <span className={`cd-level-badge cd-level-${course.level?.toLowerCase().replace(' ', '-')}`}>
                  {course.level}
                </span>
                {course.isFeatured && <span className="cd-featured-badge">⭐ Featured</span>}
              </div>

              <h1 className="cd-title">{course.title}</h1>

              {course.shortDescription && (
                <p className="cd-short-desc">{course.shortDescription}</p>
              )}

              <div className="cd-hero-meta">
                <div className="cd-rating-row">
                  <span className="cd-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <span key={s} className={s <= Math.round(course.rating || 0) ? 'star filled' : 'star'}>★</span>
                    ))}
                  </span>
                  <span className="cd-rating-val">{course.rating || 0}</span>
                </div>

                <div className="cd-instructor-row">
                  <div className="cd-instructor-avatar">{course.instructor?.charAt(0) || 'I'}</div>
                  <div>
                    <span className="cd-instructor-label">Instructor</span>
                    <span className="cd-instructor-name">{course.instructor}</span>
                  </div>
                </div>

                <div className="cd-stats-row">
                  <span className="cd-stat-item">⏱️ {course.duration}</span>
                  <span className="cd-stat-item">📊 {course.level}</span>
                  {course.tags?.[0] && (
                    <span className="cd-stat-item">🏷️ {course.tags[0].split(',')[0].trim()}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="cd-hero-image-wrap">
              <div
                className="cd-hero-image"
                style={{ background: `linear-gradient(135deg, ${categoryColor} 0%, ${categoryColor}aa 100%)` }}
              >
                {courseImage ? (
                  <img
                    src={courseImage}
                    alt={course.title}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className="cd-hero-emoji">{course.category?.icon || '📚'}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container cd-main">
        <div className="cd-body">
          {/* Left / Tab Content */}
          <div className="cd-content">
            {/* Tab Navigation */}
            <div className="cd-tabs">
              {['overview', 'curriculum', 'requirements'].map((tab) => (
                <button
                  key={tab}
                  className={`cd-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="cd-tab-content">
                {/* What You'll Learn */}
                {course.whatYouWillLearn?.length > 0 && (
                  <div className="cd-section">
                    <h2 className="cd-section-title">What You'll Learn</h2>
                    <div className="cd-learn-grid">
                      {course.whatYouWillLearn.map((item, i) => (
                        <div key={i} className="cd-learn-item">
                          <span className="cd-check">✓</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div className="cd-section">
                  <h2 className="cd-section-title">Course Description</h2>
                  <div className="cd-description">
                    {course.description.split('\n').map((para, i) =>
                      para.trim() ? <p key={i}>{para}</p> : <br key={i} />
                    )}
                  </div>
                </div>

                {/* Tags */}
                {course.tags?.length > 0 && (
                  <div className="cd-section">
                    <h2 className="cd-section-title">Tags</h2>
                    <div className="cd-tags">
                      {course.tags.flatMap((t) => t.split(',')).map((tag, i) => (
                        tag.trim() && <span key={i} className="cd-tag">{tag.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Curriculum Tab */}
            {activeTab === 'curriculum' && (
              <div className="cd-tab-content">
                {course.whatYouWillLearn?.length > 0 ? (
                  <div className="cd-section">
                    <h2 className="cd-section-title">Course Curriculum</h2>
                    <div className="cd-curriculum-list">
                      {course.whatYouWillLearn.map((item, i) => (
                        <div key={i} className="cd-curriculum-item">
                          <span className="cd-curriculum-num">{String(i + 1).padStart(2, '0')}</span>
                          <span className="cd-curriculum-icon">📖</span>
                          <span className="cd-curriculum-text">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="cd-empty">Curriculum details coming soon.</div>
                )}
              </div>
            )}

            {/* Requirements Tab */}
            {activeTab === 'requirements' && (
              <div className="cd-tab-content">
                {course.requirements?.length > 0 ? (
                  <div className="cd-section">
                    <h2 className="cd-section-title">Requirements</h2>
                    <ul className="cd-requirements-list">
                      {course.requirements.map((req, i) => (
                        <li key={i} className="cd-requirement-item">
                          <span className="cd-req-dot" style={{ backgroundColor: categoryColor }}></span>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="cd-empty">No specific requirements for this course.</div>
                )}
              </div>
            )}
          </div>

          {/* Sticky Sidebar */}
          <div className="cd-sidebar">
            <div className="cd-sidebar-card">
              {/* Price */}
              <div className="cd-price-section">
                <div className="cd-price-row">
                  <span className="cd-price">{formatPrice(course.price)}</span>
                  {course.originalPrice && course.originalPrice > course.price && (
                    <span className="cd-original-price">{formatPrice(course.originalPrice)}</span>
                  )}
                  {discount && (
                    <span className="cd-discount-badge">{discount}% OFF</span>
                  )}
                </div>
              </div>

              {/* Enroll Button */}
              {course.enrollLink ? (
                <a
                  href={course.enrollLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cd-enroll-btn"
                  style={{ background: `linear-gradient(135deg, ${categoryColor} 0%, #764ba2 100%)` }}
                >
                  Enroll Now 🚀
                </a>
              ) : (
                <button
                  className="cd-enroll-btn"
                  style={{ background: `linear-gradient(135deg, ${categoryColor} 0%, #764ba2 100%)` }}
                >
                  Enroll Now 🚀
                </button>
              )}

              {/* Syllabus Download */}
              {course.syllabusDownloadLink && (
                <a
                  href={resolveMediaUrl(course.syllabusDownloadLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cd-syllabus-btn"
                >
                  📄 Download Syllabus
                </a>
              )}

              {/* Course Info */}
              <div className="cd-info-list">
                <div className="cd-info-item">
                  <span className="cd-info-icon">⏱️</span>
                  <div>
                    <span className="cd-info-label">Duration</span>
                    <span className="cd-info-value">{course.duration}</span>
                  </div>
                </div>
                <div className="cd-info-item">
                  <span className="cd-info-icon">📊</span>
                  <div>
                    <span className="cd-info-label">Level</span>
                    <span className="cd-info-value">{course.level}</span>
                  </div>
                </div>
                <div className="cd-info-item">
                  <span className="cd-info-icon">🏷️</span>
                  <div>
                    <span className="cd-info-label">Category</span>
                    <span className="cd-info-value">{course.category?.name || 'General'}</span>
                  </div>
                </div>
                <div className="cd-info-item">
                  <span className="cd-info-icon">⭐</span>
                  <div>
                    <span className="cd-info-label">Rating</span>
                    <span className="cd-info-value">{course.rating || 0} / 5</span>
                  </div>
                </div>
                {course.isFeatured && (
                  <div className="cd-info-item">
                    <span className="cd-info-icon">🌟</span>
                    <div>
                      <span className="cd-info-label">Status</span>
                      <span className="cd-info-value cd-featured-text">Featured Course</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetail;
